import { caseItems, featuredCase } from "@/content/cases";
import { galleryPhotos, type PhotoItem } from "@/content/photo-gallery";

/**
 * Gemensam mediamodell för arbetsreelen på /case. Film och foto ligger i
 * samma lista så att fliken "Allt" kan blanda dem i samma mosaik.
 */
export type ReelVideo = {
  kind: "video";
  id: string;
  youtubeId: string;
  title: string;
};

export type ReelPhoto = {
  kind: "photo";
  id: string;
  src: string;
  alt: string;
  w: number;
  h: number;
};

export type ReelItem = ReelVideo | ReelPhoto;

/**
 * Filmer som finns i reelen men saknar eget case-uppslag. Hämtade från
 * Norrhavets arbetsreel; ID:n som redan ligger i `cases.ts` är bortplockade.
 */
const extraFilmIds: string[] = [
  "Rq9Ghsq1Lmo",
  "Odua3179y5s",
  "UxwHfbwlTYI",
  "2YCWQZnks5Q",
  "BzHML38R2bs",
  "lEr1THX_XzE",
  "8cq2SeH1AGQ",
  "HqMpIkQX5kg",
  "1iNffzbqLmU",
  "ePVXNuo62Jc",
  "q48ND68nPu8",
  "V4yA-e26XBc",
];

const caseVideos: ReelVideo[] = [featuredCase, ...caseItems].map((c) => ({
  kind: "video" as const,
  id: c.id,
  youtubeId: c.id,
  title: `${c.title} — ${c.client}`,
}));

const extraVideos: ReelVideo[] = extraFilmIds.map((id) => ({
  kind: "video" as const,
  id,
  youtubeId: id,
  title: "Filmproduktion av Fenny",
}));

function dedupe(items: ReelVideo[]): ReelVideo[] {
  const seen = new Set<string>();
  return items.filter((v) => (seen.has(v.youtubeId) ? false : (seen.add(v.youtubeId), true)));
}

/** Alla filmer i reelen — case-filmer först, därefter övriga produktioner. */
export const reelVideos: ReelVideo[] = dedupe([...caseVideos, ...extraVideos]);

const toReelPhoto = (p: PhotoItem): ReelPhoto => ({
  kind: "photo",
  id: `photo-${p.id}`,
  src: p.src,
  alt: p.alt,
  w: p.w,
  h: p.h,
});

/** Alla bilder i reelen. */
export const reelPhotos: ReelPhoto[] = galleryPhotos.map(toReelPhoto);

/**
 * Film och foto varvat — varannan film, varannan bild — så att skillnaden
 * mellan affärsområdena syns direkt i samma vy.
 */
export const reelMixed: ReelItem[] = (() => {
  const out: ReelItem[] = [];
  const max = Math.max(reelVideos.length, reelPhotos.length);
  for (let i = 0; i < max; i += 1) {
    const v = reelVideos[i];
    if (v) out.push(v);
    const p1 = reelPhotos[i * 2];
    if (p1) out.push(p1);
    const p2 = reelPhotos[i * 2 + 1];
    if (p2) out.push(p2);
  }
  return out;
})();

export const reelCounts = {
  videos: reelVideos.length,
  photos: reelPhotos.length,
};
